import { makeAutoObservable } from "mobx";
import { RemoteData } from "src/common/RemoteData";
import { cvtError, onError } from "src/common/onError";
import { ZUid } from "src/types/zUid";
import {
  combineChecked,
  groupChecked,
  GroupChecked,
  isChecked,
} from "src/common/groupChecked";
import { toggleSetItem } from "src/common/toggleSetItem";
import { Sheet2Store } from "src/components/Sheet2/Sheet2Store";
import { CtrlColumns, CtrlResizable } from "src/components/tables/TableStore";
import {
  ColumnKey,
  ColumnPoor,
  visibleFixedLeft,
} from "src/components/tables/ColumnPoor";
import { debounce, DebounceCounter } from "src/common/debounce";
import { Bom3PosColumn } from "./Bom3PosColumn";
import { createBom3Columns } from "./createBom3Columns";
import {
  ZBom3Positions,
  ZBom3PosMaterial,
  ZBom3PosPermission,
  ZBom3PosRow,
} from "./ZBom3Position";
import {
  createBom3EmptyPosition,
  deleteBom3Positions,
  loadBom3Positions,
  saveBom3Value,
  sendBom3Value,
} from "./apiBom3Positions";
import { ZBom3 } from "../ZBom3";
import { ZBom3Settings } from "../ZBom3Settings";
import {
  loadBom3PositionSettings,
  saveBom3PositionSettings,
} from "./apiBom3PositionSettings";
import { ZBom3PosSettings } from "./ZBom3PosSettings";
import {
  convertQuasiOrderToReal,
  convertRealOrderToQuasi,
  correctRealOrder,
} from "./Bom3QuasiOrder";

const defaultWidth = 200;
export const bom3PosFirstColWidth = 40;

export class Bom3PositionStore {
  constructor(
    public readonly bom: ZBom3,
    public readonly typesMap: Record<number, string>,
    public readonly settings: ZBom3Settings,
  ) {
    makeAutoObservable(this, { requestQueue: false });
  }

  static readonly minWidth = 80;

  async init() {
    try {
      this.setData({ status: "wait" });
      this.sheetStore = new Sheet2Store();
      const [result, settings] = await Promise.all([
        loadBom3Positions(this.bom.id),
        loadBom3PositionSettings(),
      ]);

      this.setData({ status: "ready", result });

      // применение настроек возможно только после инициализации данных
      this.applySettings(settings);
    } catch (e) {
      this.setData({ status: "error", error: cvtError(e) });
    }
  }

  sheetStore = new Sheet2Store();

  data: RemoteData<ZBom3Positions> = { status: "none" };

  setData(newData: RemoteData<ZBom3Positions>) {
    this.data = newData;
  }

  lockCounter = 0;

  lock() {
    this.lockCounter += 1;
  }

  unlock() {
    this.lockCounter -= 1;
  }

  get buzy(): boolean {
    return this.lockCounter !== 0;
  }

  get materials(): ZBom3PosMaterial[] {
    const { data } = this;
    return data.status === "ready" ? data.result.sections : [];
  }

  findSectionId(srcRowId: ZUid): ZUid | undefined {
    return this.materials.find(
      ({ rows }) => !!rows.find(({ rowId }) => srcRowId === rowId),
    )?.sectionId;
  }

  // Все колонки в исходном порядке
  get srcColumns(): Bom3PosColumn[] {
    return createBom3Columns(this);
  }

  get sortedColumns(): Bom3PosColumn[] {
    const orderMap = new Map<string, number>(
      this.columnsOrder.map((key, index) => [key, index]),
    );

    return this.srcColumns.slice().sort((a, b) => {
      const orderA = orderMap.get(String(a.key)) ?? Number.MAX_SAFE_INTEGER;
      const orderB = orderMap.get(String(b.key)) ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });
  }

  get columns(): Bom3PosColumn[] {
    return this.sortedColumns.filter(
      ({ key }) => !this.invisibleColumns.has(key),
    );
  }

  get columnWidthList(): number[] {
    const { columns, columnWidths } = this;
    return [
      bom3PosFirstColWidth,
      ...columns
        .map(({ key }) => columnWidths[key] ?? defaultWidth)
        .map((v) => +v),
    ];
  }

  get totalColumnsWidth() {
    return this.columnWidthList.reduce((sum, val) => sum + val, 0);
  }

  get gridTemplateColumns(): string {
    const strWidth = (w: number | string) => {
      const ws = typeof w === "number" ? `${w}px` : w;
      return `minmax(${ws}, ${ws})`;
    };

    const chunks: string[] = this.columnWidthList.map((w) => strWidth(w));
    return chunks.join(" ");
  }

  // Коллапсы для материалов
  collapsedMaterials = new Set<ZUid>();

  toggleMaterialCollapse(materialId: ZUid) {
    toggleSetItem(this.collapsedMaterials, materialId);
    this.sheetStore.postUpdate();
  }

  get canCollapseAll(): boolean {
    const { materials, collapsedMaterials, buzy } = this;
    if (buzy) return false;
    return collapsedMaterials.size < materials.length;
  }

  collapseAll() {
    const { materials } = this;
    this.collapsedMaterials = new Set(
      materials.map(({ sectionId }) => sectionId),
    );
  }

  get canExpandAll(): boolean {
    const { data, collapsedMaterials, buzy } = this;
    if (data.status !== "ready" || buzy) return false;
    return collapsedMaterials.size > 0;
  }

  expandAll() {
    this.collapsedMaterials.clear();
  }

  // Выделение строк разного уровня

  selectedRows = new Set<ZUid>();

  clearSelect() {
    this.selectedRows.clear();
  }

  toggleRowSelect(rowId: ZUid) {
    toggleSetItem(this.selectedRows, rowId);
  }

  get selectedMaterials(): Record<ZUid, GroupChecked | undefined> {
    const result: Record<ZUid, GroupChecked> = {};
    const { materials, selectedRows } = this;
    materials.forEach(({ sectionId, rows }) => {
      if (rows.length > 0) {
        result[sectionId] =
          groupChecked(rows, ({ rowId }) => selectedRows.has(rowId)) ?? "none";
      }
    });
    return result;
  }

  toggleMaterialById(dstMaterialId: ZUid) {
    const { materials } = this;
    const m = materials.find(({ sectionId }) => sectionId === dstMaterialId);
    if (m) {
      this.selectMaterial(m, !isChecked(this.selectedMaterials[dstMaterialId]));
    }
  }

  selectMaterial(material: ZBom3PosMaterial, checked: boolean) {
    const { selectedRows } = this;
    material.rows.forEach(({ rowId }) => {
      if (checked) {
        selectedRows.add(rowId);
      } else {
        selectedRows.delete(rowId);
      }
    });
  }

  get selectedAll(): GroupChecked {
    const { materials, selectedMaterials } = this;
    // Нужно игнорировать материалы, где нет строк, т.к. они всегда будут none и общий резудьтат не сможет быть checked
    return (
      materials
        .filter(({ rows }) => rows.length > 0)
        .reduce(
          (acc: GroupChecked | undefined, { sectionId }) =>
            combineChecked(acc, selectedMaterials[sectionId]),
          undefined,
        ) ?? "none"
    );
  }

  toggleSelectAll() {
    const isNewCheck = !isChecked(this.selectedAll);
    this.materials.forEach((mat) => this.selectMaterial(mat, isNewCheck));
  }

  /**
   * Специальный костыль, который выстраивает все запросы в очередь, чтобы не происходило затирание устаревшими данными
   * LPLM-5258
   */
  async queuedTask(
    task: () => Promise<ZBom3Positions>,
  ): Promise<ZBom3Positions> {
    // выполнение новой задачи начнётся только после завершения всех ранее поступивших
    const thisPromise = this.requestQueue.then(() => task());
    // если не вставить catch для каждой задачи, то любая ошибка прервёт очередь.
    // но реальный обработчик ошибок каждой отдельной задачи нужно цеплять на результат данной функции,
    // а в очереди ошибки просто игнорируются
    // eslint-disable-next-line no-console
    this.requestQueue = thisPromise.catch((e) => console.log(e));
    return thisPromise;
  }

  requestQueue: Promise<unknown> = Promise.resolve();

  async saveValue(
    rowId: ZUid,
    colId: ZUid,
    value: string | null,
  ): Promise<ZBom3Positions> {
    return this.queuedTask(() =>
      saveBom3Value(this.bom.id, rowId, colId, value),
    );
  }

  sendValue(rowId: ZUid, colId: ZUid, value: string | undefined) {
    const { materials } = this;
    let row: ZBom3PosRow | undefined;
    // eslint-disable-next-line no-restricted-syntax
    for (const mat of materials) {
      row = mat.rows.find((it) => it.rowId === rowId);
      if (row) break;
    }
    if (!row) {
      return;
    }
    const old = row.attributes[colId]?.value;
    if (value === old) return;
    if (value === "" && old === null) return;
    sendBom3Value(this.bom.id, rowId, colId, value ?? null).catch(onError);
  }

  directChangeValue(cellKey: string, value: string | null): Promise<void> {
    const { sheetStore } = this;
    sheetStore.setCurCellValue(cellKey, value);
    return sheetStore.saveCell(sheetStore.cellByKey(cellKey));
  }

  updatePositions(result: ZBom3Positions) {
    this.setData({ status: "ready", result });
  }

  // --- permissions
  possibleAction(permission: ZBom3PosPermission): boolean {
    const { data } = this;
    return (
      data.status === "ready" && data.result.permissions.includes(permission)
    );
  }

  // Все права проверяются только через UPDATE
  // http://jira.d4r.int/browse/LPLM-5254

  get possibleDelete(): boolean {
    return this.possibleUpdate;
  }

  get possibleCreate(): boolean {
    return this.possibleUpdate;
  }

  get possibleUpdate(): boolean {
    return this.possibleAction("UPDATE");
  }

  // --- tasks

  async lockingTask(task: () => Promise<ZBom3Positions>) {
    try {
      this.lock();
      const result = await this.queuedTask(task);
      this.updatePositions(result);
    } finally {
      this.unlock();
    }
  }

  createEmptyPosition(materialId: ZUid) {
    const task = async (): Promise<ZBom3Positions> => {
      const res = await createBom3EmptyPosition(this.bom.id, materialId);
      this.collapsedMaterials.delete(materialId);
      return res;
    };
    this.lockingTask(task).catch(onError);
  }

  deleteSelectedRows() {
    const rowIds: ZUid[] = Array.from(this.selectedRows);
    this.lockingTask(async () => {
      const res = await deleteBom3Positions(this.bom.id, rowIds);
      this.setDeleteConfirm(false);
      this.clearSelect();
      return res;
    }).catch(onError);
  }

  get canDelete(): boolean {
    if (this.buzy) return false;
    return this.selectedRows.size > 0;
  }

  deleteConfirm = false;

  setDeleteConfirm(open: boolean) {
    this.deleteConfirm = open;
  }

  // ---- columns controls
  invisibleColumns = new Set<ZUid>();

  setInvisibleColumns(list: ZUid[]) {
    this.invisibleColumns = new Set(list);
  }

  columnsOrder: string[] = [];

  setColumnsOrder(newOrder: string[]) {
    this.columnsOrder = newOrder;
  }

  fixedColumns = 0; // не учитывается колонка с чекбоксами (она всегда фиксирована)

  setFixedColumns(count: number) {
    this.fixedColumns = count;
  }

  get visibleFixedCount(): number {
    // реально нужно фиксировать только те колонки, которые видны
    const columns = this.sortedColumns.map(cvtCol);
    return visibleFixedLeft(columns, this.ctrlColumns);
  }

  get ctrlColumns(): CtrlColumns {
    const store = this;
    return {
      columns: store.srcColumns.map((col, i) => ({
        ...cvtCol(col),
        disabledDrag: col.isColorModel && store.srcColumns[i - 1]?.isColorModel,
      })),
      finalColumns: store.columns.map(cvtCol),
      columnOrder: store.columnsOrder,
      fixedLeftRaw: store.fixedColumns, // колонка с чекбоксами не учитывается ни в fixedColumns, ни в fixedLeftRaw
      isColumnVisible(key: ColumnKey): boolean {
        return !store.invisibleColumns.has(key);
      },
      setColumnVisible(key: ColumnKey, visible: boolean): void {
        if (visible) {
          store.invisibleColumns.delete(key);
        } else {
          store.invisibleColumns.add(key);
        }
        store.changeConfig();
      },
      canColumnHide(key: ColumnKey): boolean {
        return store.columns.length > 1 || key !== store.columns[0]?.key;
      },
      setColumnOrder(newOrder: string[]): void {
        store.setColumnsOrder(correctRealOrder(newOrder, store.srcColumns));
        store.changeConfig();
      },
      setFixedLeftRaw(count: number): void {
        store.setFixedColumns(count);
        store.changeConfig();
      },
    } satisfies CtrlColumns;
  }

  columnWidths: Record<string, number | string> = {};

  setColumnWidth(key: string, width: number | string) {
    this.columnWidths[key] = width;
  }

  setAllColumnWidths(dict: Record<string, number | string>) {
    this.columnWidths = dict;
  }

  get ctrlResizable(): CtrlResizable {
    const store = this;
    return {
      columnWidths: store.columnWidths,
      resizableColumns: true,
      setColumnWidth(key: string, width: number | string) {
        store.setColumnWidth(key, width);
        store.changeConfig();
      },
    };
  }

  changeConfig() {
    debounce(cfgCounter, 500, () => {
      this.saveSettings().catch(onError);
    });
  }

  applySettings(settings: ZBom3PosSettings | null | undefined) {
    if (settings?.columnsOrder) {
      // в настройках хранится квази-порядок
      const realOrder = convertQuasiOrderToReal(
        settings.columnsOrder,
        this.srcColumns,
      );
      this.setColumnsOrder(realOrder);
    }
    if (settings?.invisibleColumns) {
      this.setInvisibleColumns(settings.invisibleColumns);
    }
    if (settings?.columnWidths) {
      this.setAllColumnWidths(settings.columnWidths);
    }
    // Если каких-то колонок в настройках нет, то их нужно заполнить дефолтным значением. Иначе при dragStart будет сбрасываться в 0
    this.srcColumns.forEach(({ key }) => {
      if (!this.columnWidths[key]) {
        this.setColumnWidth(String(key), defaultWidth);
      }
    });
    this.setFixedColumns(settings?.fixedColumns ?? this.fixedColumns);
    this.sheetStore.postUpdate();
  }

  async saveSettings() {
    const settings: ZBom3PosSettings = {
      ver: "1",
      columnsOrder:
        this.columnsOrder.length > 0
          ? convertRealOrderToQuasi(this.columnsOrder, this.srcColumns)
          : undefined,
      invisibleColumns: Array.from(this.invisibleColumns),
      columnWidths: this.columnWidths,
      fixedColumns: this.fixedColumns,
    };
    await saveBom3PositionSettings(settings);
  }
}

const cfgCounter: DebounceCounter = {};

const cvtCol = ({ key, header }: Bom3PosColumn): ColumnPoor => ({
  key: String(key),
  title: header,
});
